import React from 'react';

import { Avatar, Box, Button, CircularProgress, Paper, Stack } from '@mui/material';

import { Icon, Typography } from '@liscio/ui';

import { ContactSuggestion } from './contactSuggestions';
import {
  getContactDisplayName,
  getContactInitials,
  groupContactSuggestions,
} from './groupContactSuggestions';
import { HighlightSearchMatch } from './HighlightSearchMatch';

export type ContactSuggestionPanelProps = {
  emailQuery: string;
  suggestions: ContactSuggestion[];
  isLoading: boolean;
  onSelectExisting: (suggestion: ContactSuggestion) => void;
  onSelectGmail: (suggestion: ContactSuggestion) => void;
  onCreateManually: () => void;
};

/** Display name of the source provider for a third-party row, or null if unknown. */
function getProviderName(
  suggestion: ContactSuggestion
): 'Gmail' | 'Outlook' | null {
  switch (suggestion.source_provider) {
    case 'google':
      return 'Gmail';
    case 'outlook':
      return 'Outlook';
    default:
      return null;
  }
}

const SuggestionRow: React.FC<{
  suggestion: ContactSuggestion;
  subtext: string;
  searchQuery: string;
  badge: React.ReactNode;
  ariaLabel: string;
  avatarColor?: string;
  onAction: () => void;
}> = ({
  suggestion,
  subtext,
  searchQuery,
  badge,
  ariaLabel,
  avatarColor = 'primary.main',
  onAction,
}) => (
  <Box
    component="button"
    type="button"
    onClick={onAction}
    aria-label={ariaLabel}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      width: '100%',
      px: 2,
      py: 1.5,
      border: 'none',
      bgcolor: 'transparent',
      cursor: 'pointer',
      textAlign: 'left',
      font: 'inherit',
      color: 'inherit',
      '&:hover': {
        bgcolor: 'action.hover',
      },
      '&:not(:last-of-type)': {
        borderBottom: '1px solid',
        borderColor: 'divider',
      },
    }}
  >
    <Avatar
      sx={{
        width: 36,
        height: 36,
        fontSize: '0.875rem',
        fontWeight: 700,
        bgcolor: avatarColor,
        flexShrink: 0,
      }}
    >
      {getContactInitials(suggestion)}
    </Avatar>

    <Stack flex={1} minWidth={0} spacing={0.25}>
      <HighlightSearchMatch
        content={getContactDisplayName(suggestion)}
        query={searchQuery}
        variant="body2"
        fontWeight={700}
        noWrap
      />
      <HighlightSearchMatch
        content={subtext}
        query={searchQuery}
        variant="caption"
        color="text.secondary"
        noWrap
      />
    </Stack>

    {badge}
  </Box>
);

export const ContactSuggestionPanel: React.FC<ContactSuggestionPanelProps> = ({
  emailQuery,
  suggestions,
  isLoading,
  onSelectExisting,
  onSelectGmail,
  onCreateManually,
}) => {
  const trimmedEmail = emailQuery.trim();
  const showCreateManually = trimmedEmail.includes('@') && !isLoading;
  const { existingContacts, gmailContacts } = groupContactSuggestions(suggestions);
  const hasResults = existingContacts.length > 0 || gmailContacts.length > 0;

  if (!isLoading && !hasResults && !showCreateManually) {
    return null;
  }

  return (
    <Paper
      elevation={4}
      sx={{
        mt: 1,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {isLoading && (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Searching contacts…
          </Typography>
        </Stack>
      )}

      {!isLoading && existingContacts.length > 0 && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              px: 2,
              pt: 1.5,
              pb: 0.5,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Existing Liscio contacts
          </Typography>
          {existingContacts.map((suggestion) => (
            <SuggestionRow
              key={`existing-${suggestion.contact_uuid ?? suggestion.contact_id ?? suggestion.primary_email}`}
              suggestion={suggestion}
              subtext={suggestion.primary_email}
              searchQuery={trimmedEmail}
              avatarColor="grey.800"
              badge={
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  Already in Liscio
                </Typography>
              }
              ariaLabel={`Existing contact ${getContactDisplayName(suggestion)}`}
              onAction={() => onSelectExisting(suggestion)}
            />
          ))}
        </Box>
      )}

      {!isLoading && gmailContacts.length > 0 && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              px: 2,
              pt: existingContacts.length > 0 ? 1 : 1.5,
              pb: 0.5,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            From your email contacts
          </Typography>
          {gmailContacts.map((suggestion) => {
            const providerName = getProviderName(suggestion);
            // Passive origin descriptor — NOT an action. "From Gmail" reads as
            // "this contact came from your email", unlike a clickable chip.
            const originText = providerName
              ? `From ${providerName}`
              : 'Email contact';
            const ariaLabel = providerName
              ? `${providerName} contact ${getContactDisplayName(suggestion)}`
              : `Email contact ${getContactDisplayName(suggestion)}`;
            return (
              <SuggestionRow
                key={`gmail-${suggestion.third_party_contact_id ?? suggestion.primary_email}`}
                suggestion={suggestion}
                searchQuery={trimmedEmail}
                subtext={
                  suggestion.phone_number
                    ? `${suggestion.primary_email} · ${suggestion.phone_number}`
                    : suggestion.primary_email
                }
                badge={
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    {originText}
                  </Typography>
                }
                ariaLabel={ariaLabel}
                onAction={() => onSelectGmail(suggestion)}
              />
            );
          })}
        </Box>
      )}

      {showCreateManually && (
        <Box
          sx={{
            borderTop: hasResults ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          <Button
            type="button"
            fullWidth
            onClick={onCreateManually}
            startIcon={<Icon icon="plus" />}
            sx={{
              justifyContent: 'flex-start',
              px: 2,
              py: 1.5,
              borderRadius: 0,
              textTransform: 'none',
              fontWeight: 600,
              color: 'primary.main',
            }}
          >
            Create contact with this email
          </Button>
        </Box>
      )}
    </Paper>
  );
};
